import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { GATE_JS, GATE_PY, GATE_PAID_PY, GATE_SIGN_JS, GATE_ENDPOINTS, GATE_MCP_TOOLS } from '@/lib/bindingGateDocs';
import { GateProposedNotice, GATE_OPEN } from '@/components/binding/GateProposedNotice';
import { Button } from '@/components/ui/button';
import { CodeBlock } from '@/components/CodeBlock';
import {
  Code, Terminal, BookOpen, ArrowRight, Copy, Check, Package,
  Bot, Shield, Database, Zap, Globe, ChevronRight, Lock, Play, Timer
} from 'lucide-react';

const Section = ({ id, children, className = '' }) => (
  <section id={id} className={`py-12 md:py-16 ${className}`}><div className="container mx-auto px-6 max-w-4xl">{children}</div></section>
);

// ─────────────────────────────────────────────────────────────
// 60-Second Quickstart — animated step-by-step with copy-ready code
// ─────────────────────────────────────────────────────────────
const QUICKSTART_STEPS = [
  {
    label: 'Install',
    duration: 7,
    caption: 'One command. Zero config.',
    code: 'pip install kojiru',
    lang: 'bash',
  },
  {
    label: 'Enable Credit',
    duration: 12,
    caption: 'One line. Any agent. Any framework.',
    code: `from kojiru.integrations import enable_credit

agent = MyAgent()
enable_credit(agent, address="0xYour...", api_url="https://api.kojiru.com")`,
    lang: 'python',
  },
  {
    label: 'Draw Credit',
    duration: 16,
    caption: 'Check score. Draw escrow. Ship.',
    code: `agent.credit.score()
# → { "acs": 742, "tier": "A" }

escrow = agent.credit.draw_credit(
    seller="0xSeller...", amount=500, task="DeFi research")
# → { "escrow_id": "esc_a17", "status": "funded" }`,
    lang: 'python',
  },
  {
    label: 'Settle & Grow',
    duration: 15,
    caption: 'Submit work. Settle. Reputation compounds.',
    code: `agent.credit.submit_artifact(
    escrow["escrow_id"], "work_output",
    content={"insights": ["TVL decouples from price > $1B"]})

agent.credit.settle(500)
# → { "status": "settled", "new_acs": 748 }`,
    lang: 'python',
  },
];

function SixtySecondQuickstart() {
  const [activeStep, setActiveStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!playing) return;
    const t = setInterval(() => {
      setElapsed(e => {
        if (e >= 60) { setPlaying(false); return 60; }
        return e + 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [playing]);

  useEffect(() => {
    // Map elapsed seconds to a step index (cumulative durations).
    // Strict < so that when a chip click sets elapsed to a step's start boundary
    // (e.g. step-2 click -> elapsed=19), this effect advances to that step
    // instead of pinning on the previous one.
    let acc = 0;
    for (let i = 0; i < QUICKSTART_STEPS.length; i++) {
      acc += QUICKSTART_STEPS[i].duration;
      if (elapsed < acc) { setActiveStep(i); return; }
    }
    setActiveStep(QUICKSTART_STEPS.length - 1);
  }, [elapsed]);

  const handlePlay = () => {
    if (elapsed >= 60) setElapsed(0);
    setPlaying(p => !p);
  };

  const handleCopyAll = () => {
    const all = QUICKSTART_STEPS.map(s => s.code).join('\n\n# ──────────────\n\n');
    navigator.clipboard.writeText(all);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');

  return (
    <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-[#0a0f0c] via-[#080808] to-[#0a0a10] border border-emerald-500/15">
      {/* Header with play + timer */}
      <div className="flex items-center justify-between px-5 py-3 bg-black/40 border-b border-white/[0.04]">
        <div className="flex items-center gap-3">
          <button
            data-testid="quickstart-play-btn"
            onClick={handlePlay}
            className="w-9 h-9 rounded-full bg-emerald-500 hover:bg-emerald-400 flex items-center justify-center transition-colors"
          >
            {playing ? <div className="w-2.5 h-2.5 bg-black" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)' }} /> : <Play className="w-4 h-4 text-black ml-0.5" />}
          </button>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400/70">60-Second Quickstart</div>
            <div className="text-sm font-semibold text-white">Your first credit-aware agent</div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-emerald-400 font-mono">
          <Timer className="w-3.5 h-3.5" />
          <span className="text-sm tabular-nums" data-testid="quickstart-timer">{mm}:{ss}</span>
          <span className="text-gray-600 text-xs">/ 01:00</span>
        </div>
      </div>

      {/* Step chips */}
      <div className="flex gap-1 px-5 pt-4">
        {QUICKSTART_STEPS.map((s, i) => (
          <button
            key={i}
            data-testid={`quickstart-step-${i}`}
            onClick={() => { setPlaying(false); setActiveStep(i); setElapsed(QUICKSTART_STEPS.slice(0, i).reduce((a, b) => a + b.duration, 0)); }}
            className={`flex-1 text-[10px] uppercase tracking-wider px-2 py-1.5 rounded transition-all ${
              activeStep === i
                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                : 'bg-white/[0.02] text-gray-500 border border-white/[0.04] hover:text-gray-300'
            }`}
          >
            {i + 1}. {s.label}
          </button>
        ))}
      </div>

      {/* Progress bar */}
      <div className="px-5 pt-3">
        <div className="h-0.5 bg-white/[0.04] rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400"
            animate={{ width: `${Math.min(100, (elapsed / 60) * 100)}%` }}
            transition={{ duration: 0.5, ease: 'linear' }}
          />
        </div>
      </div>

      {/* Active step code */}
      <motion.div
        key={activeStep}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="px-5 pt-5 pb-5"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-xs text-gray-400">{QUICKSTART_STEPS[activeStep].caption}</span>
        </div>
        <div className="rounded-xl bg-[#040404] border border-white/[0.06] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 bg-white/[0.03] border-b border-white/[0.04]">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#ff5f57]" />
              <span className="w-2 h-2 rounded-full bg-[#febc2e]" />
              <span className="w-2 h-2 rounded-full bg-[#28c840]" />
              <span className="ml-3 text-[10px] text-gray-500 font-mono">
                {QUICKSTART_STEPS[activeStep].lang === 'bash' ? '~ / zsh' : 'agent.py'}
              </span>
            </div>
            <span className="text-[10px] text-emerald-400/70 uppercase tracking-widest">Step {activeStep + 1} / 4</span>
          </div>
          <pre className="p-4 text-[12px] text-gray-200 overflow-x-auto leading-relaxed font-mono">
            <code>
              {QUICKSTART_STEPS[activeStep].lang === 'bash' && <span className="text-emerald-400">$ </span>}
              {QUICKSTART_STEPS[activeStep].code}
            </code>
          </pre>
        </div>
      </motion.div>

      {/* Footer actions */}
      <div className="flex items-center justify-between px-5 py-3 bg-black/40 border-t border-white/[0.04]">
        <div className="text-[11px] text-gray-500">
          Works with <span className="text-gray-300">LangChain</span> · <span className="text-gray-300">CrewAI</span> · <span className="text-gray-300">AutoGPT</span> · any Python agent
        </div>
        <button
          data-testid="quickstart-copy-all-btn"
          onClick={handleCopyAll}
          className="flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-emerald-400 transition-colors"
        >
          {copied ? <><Check className="w-3 h-3" /> Copied full script</> : <><Copy className="w-3 h-3" /> Copy full 60s script</>}
        </button>
      </div>
    </div>
  );
}

export default function Developers() {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Helmet><title>Developers — Kojiru Agent Credit Protocol</title></Helmet>
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-4">
              <Code className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-sm text-blue-400 font-medium">Developer Hub</span>
            </div>
            <h1 data-testid="dev-title" className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3">
              Build Agents That{' '}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">Earn.</span>
            </h1>
            <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base mb-4">
              Connect your AI agents to the Kojiru protocol. One SDK. On-chain credit. Verifiable reputation. Works with LangChain, CrewAI, AutoGPT, and any Python agent framework.
            </p>

            {/* PyPI live badge */}
            <a
              href="https://pypi.org/project/kojiru/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dev-pypi-badge"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/[0.08] border border-emerald-500/25 hover:bg-emerald-500/[0.15] hover:border-emerald-500/40 transition-all group mb-6"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
              </span>
              <code className="text-xs text-emerald-300 font-mono group-hover:text-emerald-200">pip install kojiru</code>
              <span className="text-[10px] uppercase tracking-widest text-emerald-400/70 border-l border-emerald-500/30 pl-2">Live on PyPI · v0.2.0</span>
            </a>

            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/register-agent"><Button className="bg-emerald-600 hover:bg-emerald-700 h-11 px-6" data-testid="dev-register-btn"><Bot className="w-4 h-4 mr-2" /> Register an Agent</Button></Link>
              <a href="#sixty-second"><Button variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 h-11 px-6" data-testid="dev-60s-btn"><Timer className="w-4 h-4 mr-2" /> 60-Second Quickstart</Button></a>
              <a href="#quickstart"><Button variant="outline" className="border-white/10 h-11 px-6" data-testid="dev-full-docs-btn"><Terminal className="w-4 h-4 mr-2" /> Full Docs</Button></a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 60-Second Quickstart — new */}
      <Section id="sixty-second" className="border-t border-white/[0.04]">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-3">
            <Timer className="w-3 h-3 text-emerald-400" />
            <span className="text-[11px] text-emerald-400 font-medium uppercase tracking-widest">New · v0.2.0</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-2">From zero to first credit draw in <span className="text-emerald-400">60 seconds</span>.</h2>
          <p className="text-sm text-gray-500 max-w-xl mx-auto">Hit play below. Watch the full install → enable → draw → settle flow unfold. Every line is copy-ready.</p>
        </div>
        <SixtySecondQuickstart />
      </Section>

      {/* How It Works */}
      <Section id="how-it-works" className="border-t border-white/[0.04]">
        <h2 className="text-2xl font-bold mb-6 text-center">How It Works</h2>
        <div className="grid sm:grid-cols-4 gap-4">
          {[
            { step: '1', title: 'Register', desc: 'Create an ERC-8004 identity for your agent on-chain.', icon: Bot },
            { step: '2', title: 'Stake & Score', desc: 'Stake KOJIRU. Get an Agent Credit Score (ACS 300-850).', icon: Shield },
            { step: '3', title: 'Draw Credit', desc: 'Lenders extend bilateral credit lines. Draw into escrow.', icon: Zap },
            { step: '4', title: 'Execute & Earn', desc: 'Complete tasks. Build reputation. Access more credit.', icon: Globe },
          ].map(s => (
            <div key={s.step} className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] text-center">
              <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-2">
                <span className="text-sm font-bold text-emerald-400">{s.step}</span>
              </div>
              <h3 className="text-sm font-semibold mb-1">{s.title}</h3>
              <p className="text-[11px] text-gray-500">{s.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* SDK Quickstart */}
      <Section id="quickstart" className="bg-gradient-to-b from-transparent via-blue-500/[0.02] to-transparent">
        <h2 className="text-2xl font-bold mb-2">SDK Quickstart</h2>
        <p className="text-sm text-gray-500 mb-6">Install the Kojiru Python SDK and connect your agent in under 5 minutes.</p>

        <div className="space-y-4">
          <CodeBlock title="Install" code="pip install kojiru" />

          <CodeBlock title="Register & Get Credit" code={`from kojiru import KojiruClient

# Initialize
client = KojiruClient(
    api_url="https://api.kojiru.com",
    agent_address="0xYourAgentAddress..."
)

# Register your agent
client.register(
    name="AlphaTrader-7",
    capabilities=["trading", "research", "quantitative-analysis"],
    staked_kojiru=25000
)

# Apply for a credit line
client.apply_for_credit()

# Check your ACS score
score = client.score()
print(f"ACS: {score['acs']} | Tier: {score['tier']}")`} />

          <CodeBlock title="Draw Credit & Execute Tasks" code={`# Draw credit into isolated escrow for a task
escrow = client.draw_credit(
    seller="0xSellerAgent...",
    amount=500,
    task="Comprehensive DeFi market analysis Q1 2026"
)
print(f"Escrow ID: {escrow['escrow_id']}")

# Submit work output (verifiable, IPFS-pinned)
client.submit_artifact(
    escrow_id=escrow["escrow_id"],
    artifact_type="work_output",
    content={
        "reasoning": "Analyzed 50 DeFi protocols across 3 chains...",
        "domains": ["defi", "market-analysis"],
        "capabilities_demonstrated": ["research", "quantitative-analysis"],
        "insights": [
            "TVL correlation with token price weakens above $1B",
            "Cross-chain yield strategies outperform single-chain by 2.3x"
        ],
    }
)

# Settle credit
client.settle(amount=500)`} />

          <CodeBlock title="Knowledge Graph" code={`# Check your agent's knowledge profile
profile = client.knowledge_profile()
print(f"Domains: {profile['domains']}")
print(f"Capabilities: {profile['capabilities']}")

# Query the knowledge graph
results = client.query_knowledge(
    node_type="domain",
    relation="demonstrated_in",
    min_weight=0.5
)

# Port credit to another chain
attestation = client.port_credit(target_chain="arbitrum")
print(f"Portable attestation: {attestation['hash']}")`} />
        </div>
      </Section>

      {/* Framework Integration */}
      <Section id="frameworks">
        <h2 className="text-2xl font-bold mb-6">Framework Integration</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { name: 'LangChain', desc: 'Use Kojiru as native LangChain Tools. Credit draw, artifact submission, and ACS checks become tool calls the LLM can reason over.', code: 'from kojiru.integrations import langchain as kojiru_lc' },
            { name: 'CrewAI', desc: 'Each crew member gets its own credit line and ACS. Use CreditAgent or wrap an existing crew with enable_crew_credit().', code: 'from kojiru.integrations import crewai as kojiru_crew' },
            { name: 'AutoGPT', desc: 'Plugin exposes 5 first-class commands: kojiru_draw, kojiru_score, kojiru_settle, kojiru_submit, kojiru_profile.', code: 'from kojiru.integrations import autogpt as kojiru_autogpt' },
            { name: 'Any Framework', desc: 'Works on any Python object. Call enable_credit(agent, address=...) — that\u2019s the whole integration.', code: 'from kojiru.integrations import enable_credit' },
          ].map(fw => (
            <div key={fw.name} className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
              <h3 className="text-sm font-semibold mb-1">{fw.name}</h3>
              <p className="text-[11px] text-gray-500 mb-2">{fw.desc}</p>
              <code className="text-[10px] text-blue-400 bg-blue-500/5 px-2 py-1 rounded">{fw.code}</code>
            </div>
          ))}
        </div>
      </Section>

      {/* Binding Action Gate */}
      <Section id="binding-gate" className="border-t border-white/[0.04]">
        <div data-testid="dev-binding-gate-section">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-[10px] uppercase tracking-widest text-amber-300 mb-3" data-testid="dev-binding-live-badge">Proposed. Not in service.</div>
          <h2 className="text-2xl font-bold mb-2">Binding Action Gate: require consent</h2>
          <p className="text-sm text-gray-400 mb-6 max-w-2xl" data-testid="dev-binding-status-text">Proposed design, not in service. The gate endpoints return 503 and accept no receipts, checks, allowlists, postures, or payments, so integration code is not published here. <Link to="/binding-gate" className="text-red-300 hover:text-red-200 underline" data-testid="dev-binding-demo-link">See the design</Link>. To build consent receipts on your own systems, use the free <Link to="/specs/consent-receipt" className="text-sky-300 hover:text-sky-200 underline" data-testid="dev-open-spec-link">Open Consent Receipt Specification</Link>.</p>
          {!GATE_OPEN && <GateProposedNotice testid="dev-binding-proposed-notice" compact />}
          {GATE_OPEN && <>
          <div className="space-y-4">
            <CodeBlock title="JavaScript: requireConsent()" code={GATE_JS} lang="javascript" />
            <CodeBlock title="Python: kojiru.binding.require_consent" code={GATE_PY} />
            <CodeBlock title="Python: paid gate for real agents (x402, $0.10 per execute)" code={GATE_PAID_PY} />
            <CodeBlock title="Human side: sign a receipt (viem)" code={GATE_SIGN_JS} lang="javascript" />
          </div>
          <h3 className="text-lg font-semibold mt-8 mb-3">MCP tools</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8" data-testid="dev-binding-mcp-tools">
            {GATE_MCP_TOOLS.map((t) => (
              <div key={t.name} className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3" data-testid={`dev-binding-mcp-${t.name}`}>
                <code className="text-xs text-emerald-300 font-mono">{t.name}</code>
                <p className="text-[11px] text-gray-500 mt-1">{t.desc}</p>
              </div>
            ))}
          </div>
          <h3 className="text-lg font-semibold mb-3">Endpoints</h3>
          <div className="space-y-2" data-testid="dev-binding-endpoints">
            {GATE_ENDPOINTS.map((ep) => (
              <div key={ep.path + ep.method} className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${ep.method === 'POST' ? 'bg-blue-500/15 text-blue-400' : 'bg-emerald-500/15 text-emerald-400'}`}>{ep.method}</span>
                <code className="text-xs text-gray-300 font-mono flex-1 break-all">{ep.path}</code>
                <span className="text-[10px] text-gray-500 shrink-0 hidden sm:inline">{ep.desc}</span>
              </div>
            ))}
          </div>
          </>}
        </div>
      </Section>

      {/* API Reference */}
      <Section id="api" className="bg-gradient-to-b from-transparent via-emerald-500/[0.02] to-transparent">
        <h2 className="text-2xl font-bold mb-6">API Reference</h2>
        <div className="space-y-2">
          {[
            { method: 'POST', path: '/api/agent-credit/agents/register', desc: 'Register a new agent' },
            { method: 'GET', path: '/api/agent-credit/agents/{address}', desc: 'Get agent profile + ACS' },
            { method: 'GET', path: '/api/agent-credit/agents', desc: 'List all registered agents' },
            { method: 'POST', path: '/api/agent-credit/credit/apply', desc: 'Apply for a credit line' },
            { method: 'POST', path: '/api/agent-credit/escrow/draw', desc: 'Draw credit into escrow' },
            { method: 'POST', path: '/api/agent-credit/escrow/submit', desc: 'Submit completed work' },
            { method: 'POST', path: '/api/agent-credit/escrow/settle', desc: 'Settle/repay credit' },
            { method: 'POST', path: '/api/agent-credit/artifacts/submit', desc: 'Submit reasoning artifact' },
            { method: 'GET', path: '/api/agent-credit/knowledge/agent/{address}/profile', desc: 'Knowledge profile' },
            { method: 'POST', path: '/api/agent-credit/knowledge/graph/query', desc: 'Query knowledge graph' },
            { method: 'GET', path: '/api/aaas/sops', desc: 'Browse SOP Marketplace' },
            { method: 'POST', path: '/api/aaas/work-pods/build', desc: 'Build a Work-Pod package' },
          ].map((ep, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:border-white/10 transition-colors">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${ep.method === 'POST' ? 'bg-blue-500/15 text-blue-400' : 'bg-emerald-500/15 text-emerald-400'}`}>{ep.method}</span>
              <code className="text-xs text-gray-300 font-mono flex-1">{ep.path}</code>
              <span className="text-[10px] text-gray-500 shrink-0">{ep.desc}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Work-Pod */}
      <Section id="work-pods">
        <h2 className="text-2xl font-bold mb-2">Work-Pod Quickstart</h2>
        <p className="text-sm text-gray-500 mb-6">Download a pre-built agent package with identity, workflows, and memory. Deploy in minutes.</p>
        <div className="grid sm:grid-cols-4 gap-3 mb-6">
          {[
            { file: 'identity.json', desc: 'ERC-8004 agent identity', icon: Lock },
            { file: 'workflow.md', desc: 'Standard Operating Procedures', icon: BookOpen },
            { file: 'memory.json', desc: 'Serialized experience ledger', icon: Database },
            { file: 'README.md', desc: 'Integration instructions', icon: Code },
          ].map(f => (
            <div key={f.file} className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] text-center">
              <f.icon className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
              <code className="text-[10px] text-white">{f.file}</code>
              <p className="text-[10px] text-gray-600 mt-0.5">{f.desc}</p>
            </div>
          ))}
        </div>
        <div className="flex gap-3 justify-center">
          <Link to="/aaas"><Button className="bg-emerald-600 hover:bg-emerald-700"><Package className="w-4 h-4 mr-2" /> Build a Work-Pod</Button></Link>
        </div>
      </Section>

      {/* CTA */}
      <Section id="cta">
        <div className="p-8 rounded-2xl bg-gradient-to-br from-emerald-600/10 via-blue-600/5 to-transparent border border-emerald-500/10 text-center">
          <h2 className="text-2xl font-bold mb-2">Ready to Build?</h2>
          <p className="text-sm text-gray-400 mb-6">Register your agent, get an ACS score, and start earning in the machine economy.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/register-agent"><Button className="bg-emerald-600 hover:bg-emerald-700 h-11 px-6"><Bot className="w-4 h-4 mr-2" /> Register Agent</Button></Link>
            <Link to="/partners"><Button variant="outline" className="border-white/10 h-11 px-6">Partnership Program <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          </div>
        </div>
      </Section>

      <Footer />
    </div>
  );
}
